import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {InterventionTypeDTO} from '@/ts/types/dto/today-dto';

/** DTO type for GetInterventionsResponse */
export type GetInterventionsResponse = InterventionPreview[]
/** DTO type for GetInterventionResponse */
export type GetInterventionResponse = Intervention;

/** Lifecycle and publication states of an intervention */
export enum PublicationState {
  IN_PROGRESS = 'IN_PROGRESS',
  WITHDREW = 'WITHDREW',
  COMPLETED = 'COMPLETED',
  NOT_JOINED = 'NOT_JOINED',
  ACTIVE = 'ACTIVE',
  NEW = 'NEW',
  MAINTENANCE = 'MAINTENANCE',
  DISCONTINUED = 'DISCONTINUED',
  PREVIEW = 'PREVIEW',
  PUBLISHED = 'PUBLISHED',
}

/** A cognitive distortion pattern with a name, description, and illustrative example */
export interface ThinkingTrap {
  id: UuidDTO;
  name: string;
  description?: string;
  example: string;
}

/** Summary card data for an intervention listing: name, progress, indication, and publication state */
export interface InterventionPreview {
  completionProgress: number | null;
  description: string;
  id: UuidDTO;
  name: string;
  indication: IndicationPreview
  image: UuidDTO | null;
  publicationState: PublicationState;
  /** Balm Medical or prevention, as categorised in Directus. Null when nobody categorised it. */
  type: InterventionTypeDTO | null;
}

/**
 * Per-enrollment CDC DPP status, present only for the CDC diabetes intervention once enrollment
 * data exists. The two capability flags tell the FE what to offer (render capabilities, not a
 * regime): both derive server-side from the same reward-cutoff rule that suppresses the session-8
 * reward, so they can never contradict what the backend will actually grant.
 */
export interface CdcDppStatus {
  /** Clinical eligibility outcome; null while not yet evaluated. */
  eligible: boolean | null;
  /** The enrollment carries the participation reward (reimbursement + free year at session 8). */
  participationReward: boolean;
  /** Insurance funding can be offered (past the reward cutoff and the insurance module is on). */
  insuranceFundable: boolean;
  /** The user already resolved how to continue after seeing their outcome (server-side ack). */
  eligibilityAck: boolean;
}

/** Full intervention detail including sessions, goals, citations, feelings, and thinking traps */
export interface Intervention {
  id: UuidDTO
  name: string
  description: string
  sessions: Session[]
  goals: Goal[]
  citations: Citation[]
  indication: Indication
  thinkingTraps: ThinkingTrap[]
  primaryFeelings: PrimaryFeeling[]
  image: UuidDTO;
  publicationState: PublicationState;
  // Optional as defense in depth: the backend serializes an explicit null today, but an omitted
  // key (serializer change, or the pre-cdcDpp backend below) must not read as "present".
  cdcDpp?: CdcDppStatus | null;
  /**
   * @deprecated Transitional only: what pre-cdcDpp backends send instead of `cdcDpp`. The gate
   * falls back to it (reward regime) so the eligibility dialogs never silently disappear during
   * mixed-deploy windows. Remove once every environment serves `cdcDpp`.
   */
  cdcDppEligible?: boolean | null;
}

/** Availability and completion state of an intervention session */
export enum SessionState {
  COMPLETED = 'completed',
  UNLOCK = 'unlock',
  LOCK = 'lock',
  HOMEWORK_PENDING = 'homework_pending',
  NOT_JOINED = 'not_joined',
}

/** An intervention session with its pages, homework, goals, completion status, and unlock timing */
export interface Session {
  id: UuidDTO
  /**
   * The user's row for this session, as opposed to `id`, which identifies the session's content
   * and repeats across enrollments of the same intervention. It is what tells two runs of the
   * same session apart (IPD-3030).
   *
   * Null means "no row for this session in this enrollment", which is not the same as "not
   * enrolled": a withdrawn user keeps their rows and still has an id, and a session added to the
   * catalogue after someone enrolled has no row for them yet.
   */
  // Optional, not just nullable: the backend keeps it out of the schema's `required`, so during a
  // deploy where the two sides disagree it arrives absent rather than null.
  userSessionId?: UuidDTO | null
  name: string
  description: string
  pages: Page[]
  homework: Homework[]
  goals: LayoutGoal[]
  state: SessionState
  isCompleted: boolean;
  completedDate: string
  unlockDate: string
  timeUntilUnlockMillis: number;
  image: UuidDTO;
}

/** Availability and completion state of a session page */
export enum PageState {
  LOCK = 'lock',
  UNLOCK = 'unlock',
  COMPLETED = 'completed',
}

/** A session page containing interactive components with its lock/completion state */
export interface Page {
  id: UuidDTO;
  name: string
  description: string
  image?: UuidDTO;
  components: Component[]
  state: PageState;
}

/** Types of interactive components that can appear on a session page */
export enum ComponentType {
  JOURNAL = 'journal',
  LAYOUT = 'layout',
  TABLE = 'table',
  CONTENT = 'content',
  QUESTIONNAIRE = 'questionnaire',
  PROBLEM_SOLVING_WORKSHEET = 'problem_solving_worksheet',
  SMART_CONTENT = 'smart_content',
  CONCEPTUALIZATION_WORKSHEET = 'conceptualization_worksheet',
  QUESTIONNAIRE_REVIEW = 'questionnaire_review',
  IMAGE_GRID = 'image_grid',
}

/** Types of components that can be nested within a layout component */
export enum LayoutComponentType {
  CONTENT = 'content',
  QUESTIONNAIRE = 'questionnaire',
  JOURNAL = 'journal',
  QUESTIONNAIRE_REVIEW = 'questionnaire_review',
  TABLE = 'table',
  GOAL = 'goal',
}

/** Review summary for a completed questionnaire with algorithm-based score classification */
export interface QuestionnaireReview {
  id: UuidDTO;
  reviewAlgorithm: ReviewAlgorithm;
  content?: Content;
  reviewAlgorithmScore?: ReviewAlgorithmScore;
  completed: boolean;
}

/** Algorithm used to compute questionnaire review scores */
export enum ReviewAlgorithm {
  STANDARD_REGRESSION = 'STANDARD_REGRESSION',
  SIMPLE_DIFFERENCE = 'SIMPLE_DIFFERENCE',
}

/** Outcome classification of a questionnaire review score */
export enum ReviewAlgorithmScore {
  NEUTRAL = 'NEUTRAL',
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  NO_EVIDENCE = 'NO_EVIDENCE',
}


/** A specific feeling nested under a primary feeling category */
export interface SecondaryFeeling {
  id: UuidDTO;
  name: string;
}

/** A top-level feeling category containing related secondary feelings */
export interface PrimaryFeeling {
  id: UuidDTO;
  /**
   * Stable, untranslated identifier for the family (angry, surprised, anxious,
   * rejected, sad, happy). Group by this — `name` is localized.
   */
  key?: string | null;
  name: string;
  secondaryFeelings: SecondaryFeeling[];
}

/** CBT conceptualization worksheet capturing situation, feelings, thoughts, behaviors, and coping strategies */
export interface ConceptualizationWorksheet {
  userComponentId: UuidDTO;
  contentId: UuidDTO;
  situation: ConceptualizationWorksheetData<string>;
  primaryFeelingsIds: ConceptualizationWorksheetData<UuidDTO[]>;
  secondaryFeelingsIds: ConceptualizationWorksheetData<UuidDTO[]>;
  thoughts: ConceptualizationWorksheetData<string[]>;
  physicalReactions: ConceptualizationWorksheetData<string[]>;
  thinkingTraps: ConceptualizationWorksheetData<UuidDTO[]>;
  copingThoughts: ConceptualizationWorksheetData<string[]>;
  behaviors: ConceptualizationWorksheetData<string[]>;
  questionnaire: Questionnaire;
  isAnswered: boolean;
}

/** A single field in a conceptualization worksheet with its value, visibility, and helper text */
export interface ConceptualizationWorksheetData<T> {
  value: T;
  show: boolean;
  description?: string;
  example?: string;
}

/** A single image entry within an image grid, with optional title, description, and dimensions */
export interface ImageGridItem {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  image_id: UuidDTO;
  title?: string;
  description?: string | null;
  width?: number;
  height?: number;
}

/** A grid of images displayed in a configurable rows-by-columns layout */
export interface ImageGrid {
  id: UuidDTO;
  name: string
  description: string
  // eslint-disable-next-line @typescript-eslint/naming-convention
  grid_display: string; // Format: "RowsxCols" e.g., "2x3", "3x4"
  images: ImageGridItem[];
}

/** A polymorphic page component discriminated by type, holding one of the supported content types */
export interface Component {
  type: ComponentType
  questionnaire: Questionnaire
  content: Content
  journal: Journal
  problemSolvingWorksheet: ProblemSolvingWorksheet
  conceptualizationWorksheet: ConceptualizationWorksheet
  questionnaireReview: QuestionnaireReview
  table: TableDTO
  componentLayout: Layout
  imageGrid: ImageGrid
}

/** A container layout that arranges child components in a row or column */
export interface Layout {
  contentId: string;
  componentsLayout: ILayoutComponent[];
  displayType: LayoutDisplayType;
}

/** Direction in which layout child components are arranged */
export enum LayoutDisplayType {
  ROW = 'row',
  COLUMN = 'column',
}

/** A child component within a layout, discriminated by type (content, questionnaire, journal, etc.) */
export interface ILayoutComponent {
  goal: LayoutGoal;
  type: LayoutComponentType;
  questionnaire: Questionnaire;
  content: Content;
  journal: Journal;
  questionnaireReview: QuestionnaireReview;
  table: TableDTO;
}

/** A single cell in a table, positioned by x/y coordinates with text content */
export interface TableCellDTO {
  id: UuidDTO;
  x: number;
  y: number;
  text: string;
}

/** Display mode for table components (with or without a header row) */
export enum TableDisplayTypeDTO {
  HEADER = 'HEADER',
  NO_HEADER = 'NO_HEADER',
}

/** A table component with a display mode (header/no-header) and a grid of cells */
export interface TableDTO {
  id: UuidDTO;
  displayType: TableDisplayTypeDTO;
  cells: TableCellDTO[];
}

/** Visual layout and scoring variants for questionnaires */
export enum QuestionnaireType {
  STANDARD = 'standard',
  SHOW_ONLY_QUESTIONS = 'show_only_questions',
  GRID_LAYOUT = 'grid_layout',
  WEIGHT_LOSS = 'weight_loss',
  WEIGHT_LOSS_PERCENT = 'weight_loss_percent',
}

/** A questionnaire with its questions, scoring, citations, and completion state */
export interface Questionnaire {
  id: UuidDTO
  name: string
  description: string
  questions: Question[]
  citations: Citation[]
  score: Score | null;
  hasScore: boolean;
  isAnswered: boolean;
  type: QuestionnaireType
  image: UuidDTO | null;
  /**
   * An animated illustration the CMS holds for this questionnaire, as a Directus file id. Null
   * wherever it holds none, which is most of them: the picture above is what gets drawn then.
   */
  animation?: UuidDTO | null;
  /** The card colour the CMS holds for it, as a hex string. Null lets the app choose one. */
  backgroundColor?: string | null;
}

/** A questionnaire question combining both numeric and multiple-choice capabilities */
export interface Question extends QuestionBase, QuestionMultiChoice, QuestionNumeric {
}

/** A numeric-type question with its range constraints and the user's selected value */
export interface QuestionNumeric extends QuestionBase {
  numeric: Numeric;
  numericSelected: number | null;
}

/** Common fields shared by all question types: identity, type, display text, and answer state */
export interface QuestionBase {
  userComponentId: UuidDTO
  contentId: UuidDTO
  type: QuestionType
  name: string;
  description: string;
  /** Optional explanatory text shown above the question */
  intro: string | null;
  image: UuidDTO | null;
  requiresHarmReductionCheck: boolean
  isAnswered: boolean
}

/** Input type of a questionnaire question */
export enum QuestionType {
  MULTICHOICE = 'multichoice',
  NUMERIC = 'numeric',
}

/** Visual presentation style for a multichoice question's answer options */
export enum QuestionDisplayType {
  ORDERED_LIST = 'ORDERED_LIST',
  HORIZONTAL_SLIDER = 'HORIZONTAL_SLIDER',
  CHECKBOX = 'CHECKBOX',
  GRID = 'GRID',
  VERTICAL_SLIDER = 'VERTICAL_SLIDER',
  UNORDERED_LIST = 'UNORDERED_LIST',
  IMAGE_ANSWERS = 'IMAGE_ANSWERS',
  CHECKBOX_WITH_INPUT = 'CHECKBOX_WITH_INPUT',
  DROPDOWN = 'DROPDOWN',
}

/** A multiple-choice question with its options, display style, and the user's selections */
export interface QuestionMultiChoice extends QuestionBase {
  multiChoice: MultiChoice
  displayType: QuestionDisplayType
  // The backend returns null (not []) for an unanswered multichoice question. Keep this
  // honest so every consumer is forced to handle the null case (IPD-1079).
  multiChoiceSelectedIds: UuidDTO[] | null
  customAnswer?: string
}

/** Configuration for a multiple-choice question: selectable options with min/max constraints */
export interface MultiChoice {
  maxOptions: number
  minOptions: number
  options: Option[]
}

/** A selectable answer option within a multiple-choice question */
export interface Option {
  id: UuidDTO
  text: string
  value: number
  defaultAnswer: boolean;
  image?: UuidDTO;
  needsCustomAnswer?: boolean;
}

/** Numeric input constraints defining the allowed range and display format */
export interface Numeric {
  maxValue: number
  minValue: number
  format: string
}

/** A bibliographic reference associated with an intervention or questionnaire */
export interface Citation {
  id: UuidDTO
  name: string
  reference: string
}

/** Computed questionnaire score with its numeric value, label, summary, and valid range */
export interface Score {
  score: number
  summary: string
  label: string
  maxValueInclusive: number;
  minValueInclusive: number;
}

/** A rich content block with HTML text, optional media (audio/video/image), and layout direction */
export interface Content {
  id: UuidDTO;
  name: string | null; // no_text has no name
  contentHTML: string;
  audio: UuidDTO | null;
  video: UuidDTO | null;
  image: UuidDTO | null;
  /**
   * Length in seconds of the medium the piece is consumed as (its video when it has one, otherwise
   * its audio), read by the backend from the file in the CMS (IPD-3375). Null when the piece has
   * neither, and null when the CMS holds no length for the file: not every upload carried its
   * metadata, and the cards then show the kind alone. Optional as well, like `companyImage`, so a
   * deployment that predates the field still type-checks.
   */
  duration?: number | null;
  externalLink: string | null;
  displayType: ContentDisplayType;
  isLightBoxed?: boolean;
  /**
   * The partner's logo, as a Directus asset id. Optional AND nullable: the backend leaves it out
   * of some responses and sends it as null in others (the Today payload does), and every surface
   * that draws it guards on truthiness, so the two mean the same thing on screen.
   */
  companyImage?: string | null;
  transcription?: string | null;
  // True once the user has opened the content (VIEW engagement) — drives the Read/Listened/Watched
  // marker (IPD-2943). Populated by the BE on the recommendation/home/block-content responses;
  // absent until that ships, in which case no marker renders.
  consumed?: boolean;
}

/** Text and media arrangement for content blocks */
export enum ContentDisplayType {
  TEXT_LEFT = 'TEXT_LEFT',
  TEXT_RIGHT = 'TEXT_RIGHT',
  TEXT_TOP = 'TEXT_TOP',
  TEXT_BOTTOM = 'TEXT_BOTTOM',
  NO_TEXT = 'NO_TEXT',
}

/** Input format for journal entries */
export enum JournalType {
  STANDARD = 'standard',
  MULTICHOICE = 'multichoice',
}


/** A journal prompt with its user's answer, examples, and completion state */
export interface Journal {
  contentId: UuidDTO
  userComponentId: UuidDTO
  name: string
  prompt: string
  examples: string | null;
  answer: string;
  isAnswered: boolean;
  type: JournalType;
}

/** A problem-solving worksheet with problem/goal definitions, solutions, rewards, and instructional text */
export interface ProblemSolvingWorksheet {
  problem: string
  goal: string;
  rewards: string[];
  table: TableDTO;
  solutions: Solution[]

  contentId: UuidDTO
  userComponentId: UuidDTO
  isAnswered: boolean;
  exampleGoal: string;
  exampleProblem: string;
  exampleSolution: string;
  exampleActivity: string;
  exampleRewards: string;
  descriptionGoal: string;
  descriptionProblem: string;
  descriptionSolution: string;
  descriptionActivity: string;
  descriptionRewards: string;
  solutionsInstructions: string;
  actionsInstructions: string;
}

/** A proposed solution within a problem-solving worksheet, with pros, cons, and planned activities */
export interface Solution {
  name: string
  goodThings: string
  badThings: string
  activities: string[]
}

/** A homework assignment linked to a session, with its pages and completion date */
export interface Homework {
  id: UuidDTO
  completedDate: string
  pages: Page[]
  userHomeworkId: undefined;
  contentId: UuidDTO
}


/** An intervention goal with a name and HTML-formatted description */
export interface Goal {
  id: UuidDTO
  name: string
  descriptionHTML: string
}

/** Completion state used for rendering a goal within a layout */
export enum LayoutGoalDisplayType {
  INCOMPLETE = 'incomplete',
  COMPLETE = 'complete',
}

/** A goal rendered within a layout, extended with a complete/incomplete display state */
export interface LayoutGoal extends Goal {
  displayType: LayoutGoalDisplayType
}

/**
 * The indication as a listing carries it, which is not the whole one: an intervention preview
 * travels with a shorter shape whose description is optional, where the full indication states it.
 * Mirrored separately because a preview typed as the full one promises a description the wire does
 * not send, and nothing in the app reads that field off a preview to notice.
 */
export interface IndicationPreview {
  id: UuidDTO;
  name: string;
  description?: string | null;
  citations: Citation[];
}

/** A medical indication (condition/diagnosis) that an intervention targets */
export interface Indication {
  id: UuidDTO
  name: string
  description: string
  citations: Citation[]
}

/** A user-provided free-text answer to a questionnaire question, with creation timestamp */
export interface QuestionnaireCustomAnswer {
  customAnswer: string;
  questionnaireId: UuidDTO;
  questionId: UuidDTO;
  createDate: string;
}

/** Response containing all custom free-text answers for a questionnaire */
export interface GetQuestionnaireCustomAnswersResponse {
  customAnswers: QuestionnaireCustomAnswer[];
}

/** A content block extended with user engagement data: rating, bookmark status, and tags */
export interface ComponentContent extends Content {
  rating?: 'like' | 'dislike' | 'unrated';
  isBookmarked?: boolean;
  tags?: string[];
  // Org featured content — feeds Explore's "Featured by your organization" section (IPD-2942).
  isFeatured?: boolean;
  isHomeFeatured?: boolean;
}

/** DTO type for GetBlockContentResponse */
export type GetBlockContentResponse = ComponentContent;

/** Response from accepting an intervention's stay offer (grants the fallback license). */
export interface StayOfferResponse {
  contractId: string;
}
